import { useCallback, useMemo, useState } from "react";
import { Button, Text } from "@shopify/polaris";
import StartGuide from "components/StartGuide/StartGuide";
import { eprStartGuideContent } from "components/StartGuide/content/epr";
import {
  readEprStartGuideState,
  writeEprStartGuideState,
} from "./eprStartGuideStorage";

function withCompletion(content, completed, onComplete) {
  const stepsContent = {};

  Object.entries(content.steps).forEach(([id, step]) => {
    const go = ({ navigate }) => {
      onComplete(id);
      if (step.to) navigate(step.to);
    };
    const actions = [
      {
        id: `${id}-go`,
        label: step.actionLabel,
        variant: "primary",
        onClick: go,
      },
    ];

    if (content.defaultSteps.find((item) => item.id === id)?.optional) {
      actions.push({
        id: `${id}-skip`,
        label: "Skip",
        onClick: () => onComplete(id),
      });
    }

    stepsContent[id] = {
      name: step.name,
      description: step.description,
      actions,
    };
  });

  return {
    steps: content.defaultSteps.map((step) => ({
      ...step,
      complete: Boolean(completed[step.id]),
    })),
    stepsContent,
  };
}

export default function EPRStartGuide({
  renderInline = true,
  hideWhenDismissed = false,
  showHeading = false,
}) {
  const [guideState, setGuideState] = useState(readEprStartGuideState);

  const updateState = useCallback((next) => {
    writeEprStartGuideState(next);
    setGuideState(next);
  }, []);

  const markComplete = useCallback((id) => {
    setGuideState((current) => {
      if (current.completed[id]) return current;
      const next = {
        ...current,
        completed: { ...current.completed, [id]: true },
      };
      writeEprStartGuideState(next);
      return next;
    });
  }, []);

  const { steps, stepsContent } = useMemo(
    () => withCompletion(eprStartGuideContent, guideState.completed, markComplete),
    [guideState.completed, markComplete]
  );

  const allComplete = steps.length > 0 && steps.every((step) => step.complete);

  if (hideWhenDismissed && guideState.dismissed) {
    return null;
  }

  return (
    <div className="EPRStartGuide">
      {showHeading ? (
        <div style={{ marginBottom: 12 }}>
          <Text as="h2" variant="headingMd">
            Start guide
          </Text>
        </div>
      ) : null}
      <StartGuide
        visible
        steps={steps}
        stepsContent={stepsContent}
        texts={eprStartGuideContent.texts}
        minutesPerTask={eprStartGuideContent.minutesPerTask}
        renderInline={renderInline}
      />
      {allComplete && !guideState.dismissed ? (
        <div style={{ marginTop: 12 }}>
          <Button
            variant="plain"
            onClick={() => updateState({ ...guideState, dismissed: true })}
          >
            Hide on the home page
          </Button>
        </div>
      ) : null}
      {guideState.dismissed && !hideWhenDismissed ? (
        <div style={{ marginTop: 12 }}>
          <Button
            variant="plain"
            onClick={() => updateState({ ...guideState, dismissed: false })}
          >
            Show on the home page
          </Button>
        </div>
      ) : null}
    </div>
  );
}
